import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Download, Printer, QrCode, Copy } from "lucide-react";
import { toast } from "sonner";

export const QrCard = ({
  url,
  label,
  title,
  description,
  printHeading,
  filename,
  testid,
  children,
}) => {
  const wrapRef = useRef(null);

  function canvasUrl() {
    return wrapRef.current?.querySelector("canvas")?.toDataURL("image/png");
  }

  function download() {
    const data = canvasUrl();
    if (!data) return;
    const a = document.createElement("a");
    a.href = data;
    a.download = filename;
    a.click();
    toast.success("QR code downloaded");
  }

  function copyLink() {
    navigator.clipboard.writeText(url).then(
      () => toast.success("Link copied"),
      () => toast.error("Couldn't copy — press and hold the link to copy it"),
    );
  }

  function print() {
    const data = canvasUrl();
    const w = window.open("", "_blank");
    if (!data || !w) return;
    w.document.write(`<html><head><title>Trio QR</title></head><body style="font-family:sans-serif;text-align:center;padding:40px">
      <h1 style="letter-spacing:4px;text-transform:uppercase;margin:0 0 8px">${printHeading}</h1>
      <p style="margin:0 0 24px;color:#555">Eat. Drink. Vibe.</p>
      <img src="${data}" style="width:360px;height:360px" onload="window.print()" /></body></html>`);
    w.document.close();
  }

  return (
    <div data-testid={testid} className="bg-[var(--trio-surface)] border border-[var(--trio-border)] p-8 flex flex-col sm:flex-row gap-6 items-start">
      <div ref={wrapRef} className="bg-white p-3 border border-[var(--trio-border)]">
        <QRCodeCanvas data-testid={`${testid}-code`} value={url} size={1024} level="M" includeMargin style={{ width: 160, height: 160 }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold mb-2">
          <QrCode className="w-3.5 h-3.5" /> {label}
        </div>
        <h3 className="font-display uppercase text-2xl tracking-tight mb-2">{title}</h3>
        <p className="text-sm text-[var(--trio-muted)] mb-4 max-w-md">{description}</p>
        <div className="text-xs font-mono text-[var(--trio-muted)] mb-4 break-all" data-testid={`${testid}-url`}>{url}</div>
        <div className="flex flex-wrap gap-2">
          <button onClick={copyLink} data-testid={`${testid}-copy`} className="inline-flex items-center gap-2 border border-[var(--trio-border)] hover:border-[var(--trio-primary)] hover:text-[var(--trio-primary)] px-4 py-2 text-xs uppercase tracking-widest font-bold transition-colors">
            <Copy className="w-3.5 h-3.5" /> Copy Link
          </button>
          <button onClick={download} data-testid={`${testid}-download`} className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-4 py-2 text-xs uppercase tracking-widest font-bold transition-colors">
            <Download className="w-3.5 h-3.5" /> Download PNG
          </button>
          <button onClick={print} data-testid={`${testid}-print`} className="inline-flex items-center gap-2 border border-[var(--trio-border)] hover:border-[var(--trio-primary)] hover:text-[var(--trio-primary)] px-4 py-2 text-xs uppercase tracking-widest font-bold transition-colors">
            <Printer className="w-3.5 h-3.5" /> Print Table Card
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const EventsQrCard = () => (
  <QrCard
    url={`${window.location.origin}/events`}
    label="Events & RSVP QR Code"
    title="Scan To RSVP Or Buy Tickets"
    description="Your main link for flyers, Instagram bio, and the front door. Opens the Events page where guests can RSVP or buy tickets. No staff commission."
    printHeading="Scan To RSVP"
    filename="trio-events-qr.png"
    testid="events-qr-card"
  />
);

export const MenuQrCard = () => (
  <QrCard
    url={`${window.location.origin}/menu`}
    label="Menu QR Code"
    title="Scan To See The Menu"
    description="Put this on tables, the bar, and table tents. Guests scan it with their phone camera and land right on your menu."
    printHeading="Scan For Our Menu"
    filename="trio-menu-qr.png"
    testid="menu-qr-card"
  />
);
